import hydraulicDataConfig from '@/api/config/hydraulicDataConfig'
import { queryKeys } from '@/constant/common'
import { DeleteType } from '@/types/hydraulic'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-hot-toast'
import { useConfigUI } from '../../context'
import useTranslation from '@/hooks/useTranslation'
import { getApiErrorMessage } from '@/helpers'
import {
  deleteFlowRuleWithChildren,
  type FlowRuleChildren,
} from './flowRuleCascade'
import { deleteSubPipes } from './pipeCascade'

/**
 * SCRUM-1032: the API refuses to delete a pipe that still has a flow rule on
 * it, and refuses to delete that rule while its valves and sensors are still
 * attached. Deleting the pipe therefore means unwinding the whole chain —
 * valves, sensors, rule, pipe — which the user agreed to in the confirmation.
 */
async function deleteHyraulic(
  interfaceId: string,
  id: string,
  flowRuleId?: string,
  children?: FlowRuleChildren,
): Promise<void> {
  // Sub-pipes first: the API deletes only the pipe it is given, and their
  // rows would be left behind (see pipeCascade).
  await deleteSubPipes(interfaceId, id)
  if (flowRuleId) {
    await deleteFlowRuleWithChildren(interfaceId, flowRuleId, children)
  }
  await hydraulicDataConfig.delete({ interfaceId, id })
}
const useDeleteHydraulic = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { closeModal } = useConfigUI()

  // Whatever hung off the pipe's rule went with it — or, when the cascade
  // stopped part way, some of it did: a failure refreshes the lists too, or
  // they keep showing valves that are already gone.
  const refreshCascade = () => {
    queryClient.invalidateQueries([queryKeys.hydraulic])
    queryClient.invalidateQueries([queryKeys.flowRule])
    queryClient.invalidateQueries([queryKeys.flowValve])
    queryClient.invalidateQueries([queryKeys.flowSensor])
  }

  return useMutation(
    (
      payload: DeleteType & {
        flowRuleId?: string
        children?: FlowRuleChildren
      },
    ) =>
      deleteHyraulic(
        payload.interfaceId,
        payload.id,
        payload.flowRuleId,
        payload.children,
      ),
    {
      onSuccess: () => {
        refreshCascade()
        toast.success(t('DeletePipeSuccess'))
        closeModal()
      },
      onError: (error: AxiosError<any>) => {
        refreshCascade()
        toast.error(getApiErrorMessage(error))
        closeModal()
      },
    },
  )
}

export default useDeleteHydraulic
